import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { useBookingMutations } from '@/hooks/useBookingMutations';
import { Plus, Package, Calendar as CalendarIcon } from 'lucide-react';
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface BookingServicesProps {
  booking: BookingWithFinancials;
  onUpdate: () => void | Promise<void>;
}

export const BookingServices: React.FC<BookingServicesProps> = ({ booking, onUpdate }) => {
  const { addService } = useBookingMutations();
  const [isAddingService, setIsAddingService] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    service_name: '',
    quantity: 1,
    unit_price: 0,
    total_price: 0,
    service_date: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const calculateTotalPrice = (quantity: number, unitPrice: number) => {
    return quantity * unitPrice;
  };

  const handleQuantityChange = (quantity: number) => {
    const newTotalPrice = calculateTotalPrice(quantity, serviceForm.unit_price);
    setServiceForm({ 
      ...serviceForm, 
      quantity, 
      total_price: newTotalPrice 
    });
  };

  const handleUnitPriceChange = (unitPrice: number) => {
    const newTotalPrice = calculateTotalPrice(serviceForm.quantity, unitPrice);
    setServiceForm({ 
      ...serviceForm, 
      unit_price: unitPrice, 
      total_price: newTotalPrice 
    });
  };

  const handleAddService = async () => {
    try {
      setIsLoading(true);
      await addService(booking.id, serviceForm);
      setIsAddingService(false);
      setServiceForm({
        service_name: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        service_date: '',
        notes: ''
      });
      
      // 💰 Этап 3.1: Пересчитываем финансы с учетом новой услуги
      // onUpdate в родителе вызовет пересчет через useEffect
      await onUpdate();
    } catch (error) {
      console.error('Error adding service:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getServiceStatusBadge = (status: string) => {
    const statusMap = {
      'active': { label: 'Активна', variant: 'default' as const },
      'cancelled': { label: 'Отменена', variant: 'destructive' as const },
      'completed': { label: 'Выполнена', variant: 'secondary' as const },
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, variant: 'outline' as const };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const totalServicesAmount = booking.services?.reduce((sum, service) => sum + service.total_price, 0) || 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Дополнительные услуги
            </span>
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                Итого: <span className="font-semibold">{totalServicesAmount} ₽</span>
              </div>
              <Dialog open={isAddingService} onOpenChange={setIsAddingService}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Добавить услугу
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Добавить услугу</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="service_name">Название услуги</Label>
                      <Input
                        id="service_name"
                        value={serviceForm.service_name}
                        onChange={(e) => setServiceForm({ ...serviceForm, service_name: e.target.value })}
                        placeholder="Например: Завтрак, Трансфер, Экскурсия"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="quantity">Количество</Label>
                        <Input
                          id="quantity"
                          type="number"
                          min="1"
                          value={serviceForm.quantity}
                          onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="unit_price">Цена за единицу</Label>
                        <Input
                          id="unit_price"
                          type="number"
                          step="0.01"
                          value={serviceForm.unit_price}
                          onChange={(e) => handleUnitPriceChange(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="total_price">Общая стоимость</Label>
                      <Input
                        id="total_price"
                        type="number"
                        step="0.01"
                        value={serviceForm.total_price}
                        onChange={(e) => setServiceForm({ ...serviceForm, total_price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="service_date">Дата услуги</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !serviceForm.service_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {serviceForm.service_date ? format(new Date(serviceForm.service_date), "PPP", { locale: ru }) : "Выберите дату"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-50" align="start">
                          <Calendar
                            mode="single"
                            selected={serviceForm.service_date ? new Date(serviceForm.service_date) : undefined}
                            onSelect={(date) => setServiceForm({ ...serviceForm, service_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label htmlFor="notes">Комментарий</Label>
                      <Textarea
                        id="notes"
                        value={serviceForm.notes}
                        onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })}
                        placeholder="Дополнительная информация об услуге..."
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsAddingService(false)}>
                        Отмена
                      </Button>
                      <Button 
                        onClick={handleAddService} 
                        disabled={isLoading || !serviceForm.service_name || serviceForm.total_price <= 0}
                      >
                        {isLoading ? 'Добавление...' : 'Добавить'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Услуга</TableHead>
                <TableHead>Количество</TableHead>
                <TableHead>Цена за ед.</TableHead>
                <TableHead>Общая стоимость</TableHead>
                <TableHead>Дата</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Комментарий</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {booking.services?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    Услуги не найдены
                  </TableCell>
                </TableRow>
              ) : (
                booking.services?.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell className="font-medium">
                      {service.service_name}
                    </TableCell>
                    <TableCell>
                      {service.quantity}
                    </TableCell>
                    <TableCell>
                      {service.unit_price} ₽
                    </TableCell>
                    <TableCell className="font-medium">
                      {service.total_price} ₽
                    </TableCell>
                    <TableCell>
                      {service.service_date ? new Date(service.service_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {getServiceStatusBadge(service.status)}
                    </TableCell>
                    <TableCell>
                      {service.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};